import {
  DimensionPicker,
  useCompareSelection,
  ALL_VALUE,
} from '@/components/compare/DimensionPicker'
import { SectorIncidentHeatmap } from '@/components/compare/SectorIncidentHeatmap'
import { SectorConfidenceIndex } from '@/components/compare/SectorConfidenceIndex'
import { RegulatoryExposureBySector } from '@/components/compare/RegulatoryExposureBySector'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useCompareAnalytics,
  isJurisdictionIncidents,
  isJurisdictionConfidence,
  isCountryIncidents,
  isCountryConfidence,
  isVulnClassIncidents,
  isVulnClassConfidence,
  type ConfidenceRow,
  type JurisdictionIncidentsBucket,
  type CountryIncidentsBucket,
  type VulnClassIncidentsBucket,
} from '@/hooks/compare'
import { displayMapper } from '@/lib/display-mapper'
import type { TimeRange } from '@/types/story'

export interface CompareTabProps {
  range: TimeRange
}

// KAN-237 (P3): the canonical "no jurisdiction tagged" bucket is keyed "none".
// It is shown in the heatmap (NULL-honesty) but is NOT a selectable filter.
const NO_JURISDICTION_KEY = 'none'

// KAN-237 (P4): the "unknown country" bucket (NULL victim_country) is keyed
// "unknown" (mirrors the shared UNKNOWN_COUNTRY_BUCKET sentinel). Shown in the
// breakdown (NULL-honesty) but NOT a selectable filter.
const UNKNOWN_COUNTRY_KEY = 'unknown'

// KAN-237 (P6): the "unclassified" bucket (no CWE mapping) is keyed
// "unclassified". Shown in the breakdown (NULL-honesty) but NOT highlightable as
// a selection.
const UNCLASSIFIED_VULN_CLASS_KEY = 'unclassified'

const CONFIDENCE_ORDER = [
  'established',
  'corroborated',
  'reported',
  'single_source',
  'unverified',
] as const

const CONF_COLOUR: Record<string, string> = {
  established: 'var(--chart-success-strong)',
  corroborated: 'var(--chart-success)',
  reported: 'var(--chart-caution)',
  single_source: 'var(--chart-warning)',
  unverified: 'var(--chart-muted)',
}

const CONF_LABEL: Record<string, string> = {
  established: 'Established',
  corroborated: 'Corroborated',
  reported: 'Reported',
  single_source: 'Single-source',
  unverified: 'Unverified',
}

/**
 * KAN-237 (P3): jurisdiction incidents bars. Mirrors the sector heatmap's framing
 * but keys on the canonical jurisdiction label (NOT the sector shape) and always
 * surfaces the "no jurisdiction tagged" bucket.
 */
function JurisdictionIncidents({
  data,
  selectedValue,
  isLoading,
  isError,
  onRetry,
}: {
  data: JurisdictionIncidentsBucket[]
  selectedValue: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">
          Story Coverage by Jurisdiction
        </h3>
        <Skeleton className="h-[240px] w-full" />
      </Card>
    )
  }
  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">
          Story Coverage by Jurisdiction
        </h3>
        <ErrorState
          title="Couldn't load jurisdiction incidents."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  const hasData = data.length > 0 && data.some((d) => d.count > 0)
  const max = data.reduce((m, d) => Math.max(m, d.count), 0)

  return (
    <Card data-testid="compare-jurisdiction-heatmap" className="p-4">
      <h3 className="mb-1 text-sm font-semibold">
        Story Coverage by Jurisdiction
      </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Canonical jurisdiction labels across all IntelHub stories.
        {selectedValue !== ALL_VALUE
          ? ' Your selected jurisdiction is highlighted.'
          : ''}{' '}
        The &ldquo;no jurisdiction tagged&rdquo; bucket is shown rather than dropped.
      </p>
      {!hasData ? (
        <p className="text-sm text-muted-foreground">
          No story data for the selected period.
        </p>
      ) : (
        <ul className="space-y-1.5" aria-live="polite">
          {data.map((bucket) => {
            const pct = max > 0 ? (bucket.count / max) * 100 : 0
            const highlighted =
              selectedValue === ALL_VALUE ||
              bucket.label === selectedValue ||
              bucket.jurisdiction === selectedValue
            return (
              <li
                key={bucket.jurisdiction}
                className="flex items-center gap-2 text-xs"
              >
                <span className="w-40 shrink-0 truncate text-muted-foreground">
                  {bucket.label}
                </span>
                <span className="flex h-4 flex-1 items-center">
                  <span
                    className="h-3 rounded-sm"
                    style={{
                      width: `${Math.max(pct, bucket.count > 0 ? 2 : 0)}%`,
                      backgroundColor:
                        highlighted &&
                        bucket.jurisdiction !== NO_JURISDICTION_KEY
                          ? 'var(--cat-1)'
                          : 'var(--cat-2)',
                    }}
                  />
                </span>
                <span className="w-8 shrink-0 text-right tabular-nums">
                  {bucket.count}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

/**
 * Shared confidence ladder renderer for non-sector Compare dimensions.
 * Jurisdiction and vulnerability-class both show a selected-axis distribution,
 * optional corpus baseline, and the same legend, so the segmented bar logic lives
 * here rather than being duplicated inline in each dimension card.
 */
function ConfidenceDistributionCard({
  dataTestId,
  title,
  axisLabel,
  distributionAriaLabel,
  ladder,
  baseline,
  total,
  selectedValue,
  emptyMessage,
  descriptionAll,
  descriptionSelected,
  loadingSkeletonHeight = 'h-[120px]',
  isLoading,
  isError,
  onRetry,
}: {
  dataTestId: string
  title: string
  axisLabel: string
  distributionAriaLabel: string
  ladder: ConfidenceRow[]
  baseline: ConfidenceRow[]
  total: number
  selectedValue: string
  emptyMessage: string
  descriptionAll: string
  descriptionSelected: string
  loadingSkeletonHeight?: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{title}</h3>
        <Skeleton className={`${loadingSkeletonHeight} w-full`} />
      </Card>
    )
  }
  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{title}</h3>
        <ErrorState title="Couldn't load confidence data." onRetry={onRetry} />
      </Card>
    )
  }

  const showBaseline = selectedValue !== ALL_VALUE
  const byLabel = Object.fromEntries(ladder.map((r) => [r.label, r.count]))
  const baseTotal = baseline.reduce((s, r) => s + r.count, 0)
  const byBaseline = Object.fromEntries(baseline.map((r) => [r.label, r.count]))

  return (
    <Card data-testid={dataTestId} className="p-4">
      <h3 className="mb-1 text-sm font-semibold">{title}</h3>
      <p className="mb-3 text-xs text-muted-foreground">
        {selectedValue === ALL_VALUE ? descriptionAll : descriptionSelected}
      </p>
      {total === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs text-muted-foreground">{axisLabel}</p>
            <div
              className="flex h-8 w-full overflow-hidden rounded-md"
              role="group"
              aria-label={distributionAriaLabel}
            >
              {CONFIDENCE_ORDER.map((label) => {
                const count = byLabel[label] ?? 0
                const pct = total > 0 ? (count / total) * 100 : 0
                if (pct === 0) return null
                return (
                  <div
                    key={label}
                    style={{ width: `${pct}%`, backgroundColor: CONF_COLOUR[label] }}
                    className="flex items-center justify-center text-xs text-white"
                    title={`${CONF_LABEL[label]}: ${Math.round(pct)}%`}
                  >
                    {pct > 14 ? `${Math.round(pct)}%` : ''}
                  </div>
                )
              })}
            </div>
          </div>
          {showBaseline && (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">
                Corpus baseline
              </p>
              <div
                className="flex h-4 w-full overflow-hidden rounded-md opacity-50"
                role="group"
                aria-label="Corpus-wide confidence baseline"
              >
                {CONFIDENCE_ORDER.map((label) => {
                  const count = byBaseline[label] ?? 0
                  const pct = baseTotal > 0 ? (count / baseTotal) * 100 : 0
                  if (pct === 0) return null
                  return (
                    <div
                      key={label}
                      style={{ width: `${pct}%`, backgroundColor: CONF_COLOUR[label] }}
                      title={`${CONF_LABEL[label]} (baseline): ${Math.round(pct)}%`}
                    />
                  )
                })}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {CONFIDENCE_ORDER.map((label) => (
              <span key={label} className="inline-flex items-center gap-1">
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: CONF_COLOUR[label] }}
                />
                {CONF_LABEL[label]}
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

/**
 * KAN-237 (P3): jurisdiction confidence ladder vs the corpus baseline.
 */
function JurisdictionConfidence(props: {
  ladder: ConfidenceRow[]
  baseline: ConfidenceRow[]
  total: number
  selectedValue: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  return (
    <ConfidenceDistributionCard
      dataTestId="compare-jurisdiction-confidence"
      title="Jurisdiction Confidence Index"
      axisLabel="Jurisdiction"
      distributionAriaLabel="Jurisdiction confidence distribution"
      emptyMessage="No stories in this jurisdiction for the selected period."
      descriptionAll="Corpus-wide confidence distribution."
      descriptionSelected="How well-corroborated is coverage of your jurisdiction vs the corpus baseline?"
      {...props}
    />
  )
}

/**
 * KAN-237 (P4): leak-site victim-country bars. Mirrors the jurisdiction heatmap
 * framing but keys on ISO α2 (resolved to a display name) and always surfaces
 * the "unknown country" bucket. This dimension reads leak_site_claims — victim
 * claims, NOT the article corpus — so a population disclaimer is mandatory.
 * Echo/highlight-only: the selected country is highlighted, not filtered.
 */
function CountryIncidents({
  data,
  selectedValue,
  isLoading,
  isError,
  onRetry,
}: {
  data: CountryIncidentsBucket[]
  selectedValue: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Leak-site Victim Countries</h3>
        <Skeleton className="h-[240px] w-full" />
      </Card>
    )
  }
  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Leak-site Victim Countries</h3>
        <ErrorState
          title="Couldn't load country incidents."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  const hasData = data.length > 0 && data.some((d) => d.count > 0)
  const max = data.reduce((m, d) => Math.max(m, d.count), 0)

  return (
    <Card data-testid="compare-country-heatmap" className="p-4">
      <h3 className="mb-1 text-sm font-semibold">Leak-site Victim Countries</h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Counts are leak-site victim claims (ransomware extortion postings), not
        IntelHub&rsquo;s article-corpus geography.
        {selectedValue !== ALL_VALUE
          ? ' Your selected country is highlighted.'
          : ''}{' '}
        The &ldquo;Unknown country&rdquo; bucket is shown rather than dropped.
      </p>
      {!hasData ? (
        <p className="text-sm text-muted-foreground">
          No leak-site victim claims for the selected period.
        </p>
      ) : (
        <ul className="space-y-1.5" aria-live="polite">
          {data.map((bucket) => {
            const pct = max > 0 ? (bucket.count / max) * 100 : 0
            const isUnknown = bucket.countryIso2 === UNKNOWN_COUNTRY_KEY
            const label = isUnknown
              ? displayMapper.unknownLabel('country')
              : displayMapper.country(bucket.countryIso2)
            const highlighted =
              !isUnknown &&
              (selectedValue === ALL_VALUE || bucket.countryIso2 === selectedValue)
            return (
              <li
                key={bucket.countryIso2}
                className="flex items-center gap-2 text-xs"
              >
                <span className="w-40 shrink-0 truncate text-muted-foreground">
                  {label}
                </span>
                <span className="flex h-4 flex-1 items-center">
                  <span
                    className="h-3 rounded-sm"
                    style={{
                      width: `${Math.max(pct, bucket.count > 0 ? 2 : 0)}%`,
                      backgroundColor: highlighted
                        ? 'var(--cat-1)'
                        : 'var(--cat-2)',
                    }}
                  />
                </span>
                <span className="w-8 shrink-0 text-right tabular-nums">
                  {bucket.count}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

/**
 * KAN-237 (P6): vulnerability-class incidents bars. Story counts by CWE-derived
 * class. CWE enrichment is sparse, so the caveat is mandatory and quantified
 * (Unclassified: N of M stories). Echo/highlight-only on the selected class.
 */
function VulnClassIncidents({
  data,
  totalStories,
  selectedValue,
  isLoading,
  isError,
  onRetry,
}: {
  data: VulnClassIncidentsBucket[]
  totalStories: number
  selectedValue: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">
          Story Coverage by Vulnerability Class
        </h3>
        <Skeleton className="h-[240px] w-full" />
      </Card>
    )
  }
  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">
          Story Coverage by Vulnerability Class
        </h3>
        <ErrorState
          title="Couldn't load vulnerability-class incidents."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  const hasData = data.length > 0 && data.some((d) => d.count > 0)
  const max = data.reduce((m, d) => Math.max(m, d.count), 0)
  const unclassified =
    data.find((d) => d.vulnClass === UNCLASSIFIED_VULN_CLASS_KEY)?.count ?? 0

  return (
    <Card data-testid="compare-vuln-class-heatmap" className="p-4">
      <h3 className="mb-1 text-sm font-semibold">
        Story Coverage by Vulnerability Class
      </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Vulnerability class is derived from CWE enrichment, which is incomplete.
        Stories whose CVEs have no CWE mapping (or no CVE) appear under
        &ldquo;Unclassified&rdquo; rather than being dropped
        {totalStories > 0
          ? ` (Unclassified: ${unclassified} of ${totalStories} stories)`
          : ''}
        . Treat class proportions as indicative of covered incidents, not the
        full corpus.
        {selectedValue !== ALL_VALUE
          ? ' Your selected class is highlighted.'
          : ''}
      </p>
      {!hasData ? (
        <p className="text-sm text-muted-foreground">
          No classified story data for the selected period.
        </p>
      ) : (
        <ul className="space-y-1.5" aria-live="polite">
          {data.map((bucket) => {
            const pct = max > 0 ? (bucket.count / max) * 100 : 0
            const isUnclassified =
              bucket.vulnClass === UNCLASSIFIED_VULN_CLASS_KEY
            const highlighted =
              !isUnclassified &&
              (selectedValue === ALL_VALUE || bucket.vulnClass === selectedValue)
            return (
              <li
                key={bucket.vulnClass}
                className="flex items-center gap-2 text-xs"
              >
                <span className="w-40 shrink-0 truncate text-muted-foreground">
                  {bucket.label}
                </span>
                <span className="flex h-4 flex-1 items-center">
                  <span
                    className="h-3 rounded-sm"
                    style={{
                      width: `${Math.max(pct, bucket.count > 0 ? 2 : 0)}%`,
                      backgroundColor: highlighted
                        ? 'var(--cat-1)'
                        : 'var(--cat-2)',
                    }}
                  />
                </span>
                <span className="w-8 shrink-0 text-right tabular-nums">
                  {bucket.count}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

/**
 * KAN-237 (P4): the country dimension has no confidence ladder (leak_site_claims
 * carries no confidence column, and there's no FK to the scored corpus). This
 * note fills the confidence slot honestly rather than fabricating a ladder.
 */
function CountryDataNote() {
  return (
    <Card data-testid="compare-country-note" className="p-4">
      <h3 className="mb-1 text-sm font-semibold">About the Country dimension</h3>
      <p className="text-xs text-muted-foreground">
        Country reflects which countries ransomware groups named as victims on
        leak sites — not where IntelHub coverage was written. Because it draws on
        leak-site claims rather than the scored article corpus, there is no
        confidence index for this dimension. Switch to Sector or Jurisdiction for
        corroboration-weighted confidence.
      </p>
    </Card>
  )
}

/**
 * KAN-237 (P6): vuln-class confidence ladder vs the corpus baseline. Reuses the
 * shared confidence distribution renderer used by jurisdiction to avoid duplicate
 * segmented-bar/baseline/legend logic.
 */
function VulnClassConfidence(props: {
  ladder: ConfidenceRow[]
  baseline: ConfidenceRow[]
  total: number
  selectedValue: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  return (
    <ConfidenceDistributionCard
      dataTestId="compare-vuln-class-confidence"
      title="Vulnerability-Class Confidence Index"
      axisLabel="Vulnerability class"
      distributionAriaLabel="Vulnerability-class confidence distribution"
      emptyMessage="No classified stories for this selection in the selected period."
      descriptionAll="Corpus-wide confidence distribution. Class-level confidence is limited by sparse CWE coverage."
      descriptionSelected="How well-corroborated is coverage of your selected class vs the corpus baseline? Limited by sparse CWE coverage."
      {...props}
    />
  )
}

export function CompareTab({ range }: CompareTabProps) {
  const { dimension, value, setDimension, setValue } = useCompareSelection()
  const { data, status, refetch } = useCompareAnalytics(range, dimension, value)

  const onRetry = () => void refetch()

  const incidents = data.incidents
  const confidence = data.confidence

  // Canonical jurisdiction labels present in the data drive the value-select
  // options (exclude the non-selectable "no jurisdiction tagged" bucket).
  const jurisdictionOptions = isJurisdictionIncidents(incidents)
    ? incidents.buckets
        .filter((b) => b.jurisdiction !== NO_JURISDICTION_KEY)
        .map((b) => b.label)
    : []

  // ISO α2 codes present in the data drive the country value-select options
  // (exclude the non-selectable "unknown country" bucket). KAN-237 P4.
  const countryOptions = isCountryIncidents(incidents)
    ? incidents.buckets
        .filter((b) => b.countryIso2 !== UNKNOWN_COUNTRY_KEY)
        .map((b) => b.countryIso2)
    : []

  // vuln-class incidents carry the corpus total for the coverage caveat.
  const vulnClassTotalStories = isVulnClassIncidents(incidents)
    ? incidents.totalStories
    : 0

  const isSectorIncidents =
    !isJurisdictionIncidents(incidents) &&
    !isCountryIncidents(incidents) &&
    !isVulnClassIncidents(incidents)
  const isSectorConfidence =
    !isJurisdictionConfidence(confidence) &&
    !isCountryConfidence(confidence) &&
    !isVulnClassConfidence(confidence)

  return (
    <div className="flex flex-col gap-4">
      {/* Picker header */}
      <div className="flex items-center justify-between">
        <DimensionPicker
          dimension={dimension}
          value={value}
          onDimensionChange={setDimension}
          onValueChange={setValue}
          jurisdictionOptions={jurisdictionOptions}
          countryOptions={countryOptions}
        />
      </div>

      {/* Incidents — full width. Render the shape that matches the dimension. */}
      {dimension === 'country' ? (
        <CountryIncidents
          data={isCountryIncidents(incidents) ? incidents.buckets : []}
          selectedValue={value}
          isLoading={status.incidents.isLoading}
          isError={status.incidents.isError}
          onRetry={onRetry}
        />
      ) : dimension === 'vuln-class' ? (
        <VulnClassIncidents
          data={isVulnClassIncidents(incidents) ? incidents.buckets : []}
          totalStories={vulnClassTotalStories}
          selectedValue={value}
          isLoading={status.incidents.isLoading}
          isError={status.incidents.isError}
          onRetry={onRetry}
        />
      ) : dimension === 'jurisdiction' ? (
        <JurisdictionIncidents
          data={isJurisdictionIncidents(incidents) ? incidents.buckets : []}
          selectedValue={value}
          isLoading={status.incidents.isLoading}
          isError={status.incidents.isError}
          onRetry={onRetry}
        />
      ) : (
        <SectorIncidentHeatmap
          data={isSectorIncidents ? (incidents?.buckets ?? []) : []}
          selectedSector={value}
          isLoading={status.incidents.isLoading}
          isError={status.incidents.isError}
          onRetry={onRetry}
        />
      )}

      {/* Confidence + regulatory exposure — two-column grid. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {dimension === 'country' ? (
          // Country (leak-site) has no confidence ladder — show an honest note.
          <CountryDataNote />
        ) : dimension === 'vuln-class' ? (
          <VulnClassConfidence
            ladder={
              isVulnClassConfidence(confidence)
                ? confidence.vulnClassLadder
                : []
            }
            baseline={
              isVulnClassConfidence(confidence) ? confidence.corpusLadder : []
            }
            total={
              isVulnClassConfidence(confidence)
                ? confidence.totalVulnClass
                : 0
            }
            selectedValue={value}
            isLoading={status.confidence.isLoading}
            isError={status.confidence.isError}
            onRetry={onRetry}
          />
        ) : dimension === 'jurisdiction' ? (
          <JurisdictionConfidence
            ladder={
              isJurisdictionConfidence(confidence)
                ? confidence.jurisdictionLadder
                : []
            }
            baseline={
              isJurisdictionConfidence(confidence)
                ? confidence.corpusLadder
                : []
            }
            total={
              isJurisdictionConfidence(confidence)
                ? confidence.totalJurisdiction
                : 0
            }
            selectedValue={value}
            isLoading={status.confidence.isLoading}
            isError={status.confidence.isError}
            onRetry={onRetry}
          />
        ) : (
          <SectorConfidenceIndex
            sectorLadder={
              isSectorConfidence ? (confidence?.sectorLadder ?? []) : []
            }
            corpusLadder={
              isSectorConfidence ? (confidence?.corpusLadder ?? []) : []
            }
            totalSector={
              isSectorConfidence ? (confidence?.totalSector ?? 0) : 0
            }
            selectedSector={value}
            isLoading={status.confidence.isLoading}
            isError={status.confidence.isError}
            onRetry={onRetry}
          />
        )}
        {/* Regulatory exposure is always corpus-by-sector, independent of axis. */}
        <RegulatoryExposureBySector
          data={data.regulatory?.buckets ?? []}
          isLoading={status.regulatory.isLoading}
          isError={status.regulatory.isError}
          onRetry={onRetry}
        />
      </div>
    </div>
  )
}
